import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useBackdropClose } from "@/lib/useBackdropClose";
import SignaturePad from "@/components/SignaturePad";
import { Plus, Trash, Download, ListChecks, ChatCircleDots, ShieldWarning, BookOpen, Warning } from "@phosphor-icons/react";

const downloadPdf = async (url, filename) => {
    const r = await api.get(url, { responseType: "blob" });
    const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = blobUrl; a.download = filename; a.click();
    window.URL.revokeObjectURL(blobUrl);
};

const TABS = [
    { key: "checklists", label: "Checklists", icon: ListChecks },
    { key: "charlas", label: "Charlas Pre-turno", icon: ChatCircleDots },
    { key: "pts", label: "PTS", icon: ShieldWarning },
    { key: "bitacoras", label: "Bitácora HSE", icon: BookOpen },
    { key: "actos", label: "Actos/Condiciones", icon: Warning },
];

export default function DocumentosSST({ clientId, canCreate, tipoProyectoSugerido }) {
    const [tab, setTab] = useState("checklists");
    const [checklists, setChecklists] = useState([]);
    const [charlas, setCharlas] = useState([]);
    const [ptsList, setPtsList] = useState([]);
    const [bitacoras, setBitacoras] = useState([]);
    const [actos, setActos] = useState([]);
    const [modal, setModal] = useState(null); // "checklist" | "charla" | "pts" | "bitacora" | "acto"

    const loadAll = async () => {
        const [ck, ch, pt, bi, ac] = await Promise.all([
            api.get(`/clients/${clientId}/checklists`).catch(() => ({ data: [] })),
            api.get(`/clients/${clientId}/charlas-preturno`).catch(() => ({ data: [] })),
            api.get(`/clients/${clientId}/pts`).catch(() => ({ data: [] })),
            api.get(`/clients/${clientId}/bitacoras-hse`).catch(() => ({ data: [] })),
            api.get(`/clients/${clientId}/reportes-subestandar`).catch(() => ({ data: [] })),
        ]);
        setChecklists(ck.data); setCharlas(ch.data); setPtsList(pt.data); setBitacoras(bi.data); setActos(ac.data);
    };
    useEffect(() => { loadAll(); }, [clientId]);

    const Row = ({ title, sub, badge, onDownload }) => (
        <div className="p-3 flex items-center justify-between text-sm border-b border-zinc-100 last:border-b-0">
            <div>
                <span className="font-semibold">{title}</span>
                <span className="text-zinc-400 text-xs ml-2 font-mono">{sub}</span>
            </div>
            <div className="flex items-center gap-3">
                {badge}
                <button onClick={onDownload} className="text-zinc-400 hover:text-teal-600" title="Descargar PDF">
                    <Download size={16} />
                </button>
            </div>
        </div>
    );

    const Empty = ({ text }) => <div className="text-sm text-zinc-400 italic p-4 text-center border border-dashed border-zinc-200">{text}</div>;

    return (
        <div className="border border-zinc-200 p-6 mb-8" data-testid="documentos-sst">
            <div className="overline mb-1" style={{ color: "#0F766E" }}>Documentos SST diarios</div>
            <h2 className="font-display text-xl font-bold tracking-tight mb-4">Bitácora de obra</h2>

            <div className="flex gap-1 mb-4 border-b border-zinc-200 overflow-x-auto">
                {TABS.map(t => {
                    const Icon = t.icon;
                    return (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px flex items-center gap-1.5 whitespace-nowrap ${tab === t.key ? "border-teal-500 text-zinc-900" : "border-transparent text-zinc-500"}`}
                            data-testid={`sst-tab-${t.key}`}>
                            <Icon size={14} /> {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "checklists" && (
                <div>
                    {canCreate && <button onClick={() => setModal("checklist")} className="btn-secondary text-sm mb-3 flex items-center gap-1.5" data-testid="new-checklist-btn"><Plus size={14} /> Nuevo checklist</button>}
                    {checklists.length === 0 ? <Empty text="Sin checklists registrados." /> : (
                        <div className="border border-zinc-200">
                            {checklists.map(ck => (
                                <Row key={ck.checklist_id} title={ck.tipo_label} sub={`${ck.fecha?.slice(0, 10)} · ${ck.supervisor_nombre}`}
                                    badge={ck.cantidad_no > 0
                                        ? <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-red-500 bg-red-50 text-red-800">{ck.cantidad_no} HALLAZGO(S)</span>
                                        : <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-emerald-500 bg-emerald-50 text-emerald-800">SIN HALLAZGOS</span>}
                                    onDownload={() => downloadPdf(`/checklists/${ck.checklist_id}/pdf`, `checklist_${ck.checklist_id}.pdf`)} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {tab === "charlas" && (
                <div>
                    {canCreate && <button onClick={() => setModal("charla")} className="btn-secondary text-sm mb-3 flex items-center gap-1.5" data-testid="new-charla-btn"><Plus size={14} /> Nueva charla pre-turno</button>}
                    {charlas.length === 0 ? <Empty text="Sin charlas pre-turno registradas." /> : (
                        <div className="border border-zinc-200">
                            {charlas.map(ch => (
                                <Row key={ch.charla_id} title={ch.tema} sub={`${ch.fecha?.slice(0, 10)} · ${ch.siso_nombre} · ${ch.asistentes.length} asistentes`}
                                    onDownload={() => downloadPdf(`/charlas-preturno/${ch.charla_id}/pdf`, `charla_${ch.charla_id}.pdf`)} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {tab === "pts" && (
                <div>
                    {canCreate && <button onClick={() => setModal("pts")} className="btn-secondary text-sm mb-3 flex items-center gap-1.5" data-testid="new-pts-btn"><Plus size={14} /> Nuevo PTS</button>}
                    {ptsList.length === 0 ? <Empty text="Sin permisos de trabajo seguro registrados." /> : (
                        <div className="border border-zinc-200">
                            {ptsList.map(p => (
                                <Row key={p.pts_id} title={PTS_LABELS[p.tipo_riesgo] || p.tipo_riesgo} sub={`${p.fecha?.slice(0, 10)} · ${p.siso_nombre} · ${p.trabajadores_autorizados.length} autorizados`}
                                    onDownload={() => downloadPdf(`/pts/${p.pts_id}/pdf`, `pts_${p.pts_id}.pdf`)} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {tab === "bitacoras" && (
                <div>
                    {canCreate && <button onClick={() => setModal("bitacora")} className="btn-secondary text-sm mb-3 flex items-center gap-1.5" data-testid="new-bitacora-btn"><Plus size={14} /> Nueva bitácora del día</button>}
                    {bitacoras.length === 0 ? <Empty text="Sin bitácoras registradas." /> : (
                        <div className="border border-zinc-200">
                            {bitacoras.map(b => (
                                <Row key={b.bitacora_id} title={`Bitácora ${b.fecha?.slice(0, 10)}`} sub={`${b.siso_nombre} · ${b.numero_trabajadores} trabajadores`}
                                    onDownload={() => downloadPdf(`/bitacoras-hse/${b.bitacora_id}/pdf`, `bitacora_${b.bitacora_id}.pdf`)} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {tab === "actos" && (
                <div>
                    {canCreate && <button onClick={() => setModal("acto")} className="btn-secondary text-sm mb-3 flex items-center gap-1.5" data-testid="new-acto-btn"><Plus size={14} /> Nuevo reporte</button>}
                    {actos.length === 0 ? <Empty text="Sin actos o condiciones subestándar reportados." /> : (
                        <div className="border border-zinc-200">
                            {actos.map(a => (
                                <Row key={a.reporte_id} title={a.tipo === "acto" ? "Acto subestándar" : "Condición subestándar"} sub={`${a.fecha?.slice(0, 10)} · ${a.siso_nombre}`}
                                    onDownload={() => downloadPdf(`/reportes-subestandar/${a.reporte_id}/pdf`, `reporte_${a.reporte_id}.pdf`)} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {modal === "checklist" && <ChecklistModal clientId={clientId} tipoProyectoSugerido={tipoProyectoSugerido} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadAll(); }} />}
            {modal === "charla" && <CharlaModal clientId={clientId} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadAll(); }} />}
            {modal === "pts" && <PtsModal clientId={clientId} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadAll(); }} />}
            {modal === "bitacora" && <BitacoraModal clientId={clientId} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadAll(); }} />}
            {modal === "acto" && <ActoModal clientId={clientId} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadAll(); }} />}
        </div>
    );
}

const PTS_LABELS = {
    alturas: "Trabajo en Alturas", espacios_confinados: "Espacios Confinados",
    trabajo_caliente: "Trabajo en Caliente", excavaciones: "Excavaciones", izaje_cargas: "Izaje de Cargas",
};

function ModalShell({ title, subtitle, onClose, children, footer }) {
    const backdrop = useBackdropClose(onClose);
    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" {...backdrop.backdropProps}>
            <div className="bg-white max-w-3xl w-full max-h-[92vh] overflow-y-auto" {...backdrop.contentProps}>
                <div className="p-6 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="overline mb-1" style={{ color: "#0F766E" }}>{subtitle}</div>
                        <h2 className="font-display text-2xl font-black tracking-tighter">{title}</h2>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-3xl leading-none">×</button>
                </div>
                <div className="p-6">{children}</div>
                {footer && <div className="p-6 border-t border-zinc-200 flex justify-end gap-3 sticky bottom-0 bg-white">{footer}</div>}
            </div>
        </div>
    );
}

/* ---- 1) Checklist (con firma) ---- */
function ChecklistModal({ clientId, tipoProyectoSugerido, onClose, onSaved }) {
    const [templates, setTemplates] = useState(null);
    const [tipo, setTipo] = useState("");
    const [items, setItems] = useState([]);
    const [observacionesGenerales, setObservacionesGenerales] = useState("");
    const [firma, setFirma] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => { api.get("/checklists/templates").then(r => setTemplates(r.data)).catch(() => setTemplates({})); }, []);

    const selectTipo = (t) => {
        setTipo(t);
        setItems((templates[t]?.items || []).map(plantilla => ({ item: plantilla.item, categoria: plantilla.categoria || "General", estado: "", observacion: "", foto_base64: "" })));
    };
    const setEstado = (idx, estado) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, estado, observacion: estado === "no" ? it.observacion : "" } : it));
    const setObs = (idx, observacion) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, observacion } : it));
    const setFoto = (idx, foto_base64) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, foto_base64 } : it));
    const onFotoChange = (idx, e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setFoto(idx, reader.result);
        reader.readAsDataURL(file);
    };

    const faltanEstados = items.some(it => !it.estado);
    const faltanObs = items.some(it => it.estado === "no" && !it.observacion.trim());

    const submit = async () => {
        setLoading(true); setError("");
        try {
            await api.post("/checklists", { client_id: clientId, tipo, items: items.map(it => ({ estado: it.estado, observacion: it.observacion, foto_base64: it.foto_base64 })), observaciones_generales: observacionesGenerales, firma_supervisor: firma });
            onSaved();
        } catch (e) { setError(e.response?.data?.detail || "Error"); setLoading(false); }
    };

    const estadoBtn = (idx, valor, label, color) => (
        <button type="button" onClick={() => setEstado(idx, valor)} className="px-3 py-1 text-xs font-mono font-bold border"
            style={items[idx].estado === valor ? { background: color, borderColor: color, color: "#fff" } : { background: "#fff", borderColor: "#e4e4e7", color: "#71717a" }}
            data-testid={`item-${idx}-${valor}`}>{label}</button>
    );

    // Grupos por categoría (mismo orden en que vienen del template)
    const grupos = [];
    let ultimaCategoria = null;
    items.forEach((it, idx) => {
        if (it.categoria !== ultimaCategoria) {
            grupos.push({ categoria: it.categoria, indices: [idx] });
            ultimaCategoria = it.categoria;
        } else {
            grupos[grupos.length - 1].indices.push(idx);
        }
    });

    const TIPOS_PROYECTO = ["remodelacion", "mantenimiento_edificacion", "obra_nueva_tradicional", "steel_frame"];
    const TIPOS_GENERALES = ["sst", "equipos_diario"];

    return (
        <ModalShell title={tipo ? templates[tipo].label : "Selecciona el tipo de checklist"} subtitle="Checklist de Visita de Supervisión Técnica Ing/Arq." onClose={onClose}
            footer={tipo && (
                <>
                    <button type="button" onClick={() => setTipo("")} className="btn-secondary text-sm mr-auto">← Cambiar tipo</button>
                    <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
                    <button type="button" onClick={submit} disabled={faltanEstados || faltanObs || loading} className="btn-accent" data-testid="confirm-checklist">{loading ? "Guardando…" : "Guardar checklist →"}</button>
                </>
            )}>
            {!templates ? <div className="text-sm text-zinc-400">Cargando…</div> : !tipo ? (
                <div className="space-y-5">
                    {tipoProyectoSugerido && templates[tipoProyectoSugerido] && (
                        <button onClick={() => selectTipo(tipoProyectoSugerido)} className="w-full border-2 border-teal-500 bg-teal-50 p-5 text-left transition-colors" data-testid={`tipo-sugerido-${tipoProyectoSugerido}`}>
                            <div className="text-[10px] font-mono font-bold uppercase tracking-wide text-teal-700 mb-1">✓ Tipo de proyecto de este cliente — recomendado</div>
                            <div className="font-display text-lg font-bold mb-1">{templates[tipoProyectoSugerido].label}</div>
                            <div className="text-xs text-zinc-500">{templates[tipoProyectoSugerido].items.length} ítems a verificar</div>
                        </button>
                    )}
                    <div>
                        <div className="overline mb-2">Por tipo de proyecto</div>
                        <div className="grid md:grid-cols-2 gap-3">
                            {TIPOS_PROYECTO.filter(k => templates[k]).map(key => (
                                <button key={key} onClick={() => selectTipo(key)} className="border-2 border-zinc-200 hover:border-teal-500 p-5 text-left transition-colors" data-testid={`tipo-${key}`}>
                                    <div className="font-display text-lg font-bold mb-1">{templates[key].label}</div>
                                    <div className="text-xs text-zinc-500">{templates[key].items.length} ítems a verificar</div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="overline mb-2">Generales</div>
                        <div className="grid md:grid-cols-2 gap-3">
                            {TIPOS_GENERALES.filter(k => templates[k]).map(key => (
                                <button key={key} onClick={() => selectTipo(key)} className="border-2 border-zinc-200 hover:border-teal-500 p-5 text-left transition-colors" data-testid={`tipo-${key}`}>
                                    <div className="font-display text-lg font-bold mb-1">{templates[key].label}</div>
                                    <div className="text-xs text-zinc-500">{templates[key].items.length} ítems a verificar</div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    {grupos.map(grupo => (
                        <div key={grupo.categoria}>
                            <div className="text-[10px] font-mono font-bold uppercase tracking-wide text-teal-700 mb-2">▸ {grupo.categoria}</div>
                            <div className="space-y-3">
                                {grupo.indices.map(idx => {
                                    const it = items[idx];
                                    return (
                                        <div key={idx} className="border border-zinc-200 p-3" data-testid={`checklist-item-${idx}`}>
                                            <div className="flex items-start justify-between gap-3 mb-2">
                                                <div className="text-sm font-medium flex-1">{idx + 1}. {it.item}</div>
                                                <div className="flex gap-1.5 shrink-0">{estadoBtn(idx, "si", "SÍ", "#16A34A")}{estadoBtn(idx, "no", "NO", "#DC2626")}{estadoBtn(idx, "no_aplica", "N/A", "#71717A")}</div>
                                            </div>
                                            {it.estado === "no" && (
                                                <div className="space-y-2">
                                                    <input value={it.observacion} onChange={e => setObs(idx, e.target.value)} placeholder="Describe la observación (obligatorio)" className="input-atia w-full text-sm" data-testid={`item-${idx}-observacion`} />
                                                    <div>
                                                        <label className="text-[11px] text-zinc-500 flex items-center gap-1.5 cursor-pointer">
                                                            📷 {it.foto_base64 ? "Cambiar foto de evidencia" : "Agregar foto de evidencia (opcional)"}
                                                            <input type="file" accept="image/*" onChange={e => onFotoChange(idx, e)} className="hidden" data-testid={`item-${idx}-foto`} />
                                                        </label>
                                                        {it.foto_base64 && <img src={it.foto_base64} alt="evidencia" className="mt-1 max-h-28 border border-zinc-200" />}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                    <div>
                        <label className="overline block mb-2">Observaciones generales (opcional)</label>
                        <textarea value={observacionesGenerales} onChange={e => setObservacionesGenerales(e.target.value)} rows={3} className="input-atia w-full" />
                    </div>
                    <SignaturePad label="Firma del responsable" value={firma} onChange={setFirma} testid="firma-checklist" />
                    {error && <div className="text-xs text-red-600 font-mono">{error}</div>}
                </div>
            )}
        </ModalShell>
    );
}

/* ---- 2) Charla Pre-turno (multi-firma) ---- */
function CharlaModal({ clientId, onClose, onSaved }) {
    const [tema, setTema] = useState("");
    const [duracion, setDuracion] = useState(10);
    const [sisoFirma, setSisoFirma] = useState("");
    const [asistentes, setAsistentes] = useState([{ nombre: "", cedula: "", firma: "" }]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const addAsistente = () => setAsistentes(prev => [...prev, { nombre: "", cedula: "", firma: "" }]);
    const delAsistente = (idx) => setAsistentes(prev => prev.filter((_, i) => i !== idx));
    const setAsistente = (idx, field, value) => setAsistentes(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));

    const valido = tema && sisoFirma && asistentes.every(a => a.nombre && a.firma);

    const submit = async () => {
        setLoading(true); setError("");
        try {
            await api.post("/charlas-preturno", { client_id: clientId, tema, duracion_minutos: duracion, siso_firma: sisoFirma, asistentes });
            onSaved();
        } catch (e) { setError(e.response?.data?.detail || "Error"); setLoading(false); }
    };

    return (
        <ModalShell title="Charla Pre-turno" subtitle="Nuevo registro diario" onClose={onClose}
            footer={<>
                <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
                <button type="button" onClick={submit} disabled={!valido || loading} className="btn-accent" data-testid="confirm-charla">{loading ? "Guardando…" : "Guardar charla →"}</button>
            </>}>
            <div className="space-y-4">
                <div className="grid md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                        <label className="overline block mb-1">Tema de la charla</label>
                        <input value={tema} onChange={e => setTema(e.target.value)} placeholder="Ej. Trabajo seguro en alturas" className="input-atia w-full" data-testid="charla-tema" />
                    </div>
                    <div>
                        <label className="overline block mb-1">Duración (min)</label>
                        <input type="number" value={duracion} onChange={e => setDuracion(parseInt(e.target.value) || 0)} className="input-atia w-full" />
                    </div>
                </div>

                <SignaturePad label="Firma del SISO responsable" value={sisoFirma} onChange={setSisoFirma} testid="firma-siso-charla" />

                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="overline">Asistencia y firmas de trabajadores ({asistentes.length})</label>
                        <button type="button" onClick={addAsistente} className="btn-secondary text-xs flex items-center gap-1"><Plus size={12} /> Agregar trabajador</button>
                    </div>
                    <div className="space-y-3">
                        {asistentes.map((a, idx) => (
                            <div key={idx} className="border border-zinc-200 p-3" data-testid={`asistente-${idx}`}>
                                <div className="flex gap-2 mb-2">
                                    <input value={a.nombre} onChange={e => setAsistente(idx, "nombre", e.target.value)} placeholder="Nombre completo" className="input-atia flex-1 text-sm" />
                                    <input value={a.cedula} onChange={e => setAsistente(idx, "cedula", e.target.value)} placeholder="Cédula (opcional)" className="input-atia w-40 text-sm" />
                                    {asistentes.length > 1 && <button type="button" onClick={() => delAsistente(idx)} className="text-red-500 hover:text-red-700"><Trash size={16} /></button>}
                                </div>
                                <SignaturePad value={a.firma} onChange={(v) => setAsistente(idx, "firma", v)} testid={`firma-asistente-${idx}`} compact />
                            </div>
                        ))}
                    </div>
                </div>
                {error && <div className="text-xs text-red-600 font-mono">{error}</div>}
            </div>
        </ModalShell>
    );
}

/* ---- 3) PTS - Permiso de Trabajo Seguro (multi-firma) ---- */
function PtsModal({ clientId, onClose, onSaved }) {
    const [tipoRiesgo, setTipoRiesgo] = useState("alturas");
    const [descripcion, setDescripcion] = useState("");
    const [medidas, setMedidas] = useState("");
    const [horaInicio, setHoraInicio] = useState("");
    const [horaFin, setHoraFin] = useState("");
    const [sisoFirma, setSisoFirma] = useState("");
    const [trabajadores, setTrabajadores] = useState([{ nombre: "", cedula: "", firma: "" }]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const addTrabajador = () => setTrabajadores(prev => [...prev, { nombre: "", cedula: "", firma: "" }]);
    const delTrabajador = (idx) => setTrabajadores(prev => prev.filter((_, i) => i !== idx));
    const setTrabajador = (idx, field, value) => setTrabajadores(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));

    const valido = descripcion && sisoFirma && trabajadores.every(t => t.nombre && t.firma);

    const submit = async () => {
        setLoading(true); setError("");
        try {
            await api.post("/pts", { client_id: clientId, tipo_riesgo: tipoRiesgo, descripcion_trabajo: descripcion, medidas_control: medidas, hora_inicio: horaInicio, hora_fin: horaFin, siso_firma: sisoFirma, trabajadores_autorizados: trabajadores });
            onSaved();
        } catch (e) { setError(e.response?.data?.detail || "Error"); setLoading(false); }
    };

    return (
        <ModalShell title="Permiso de Trabajo Seguro" subtitle="Nuevo PTS" onClose={onClose}
            footer={<>
                <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
                <button type="button" onClick={submit} disabled={!valido || loading} className="btn-accent" data-testid="confirm-pts">{loading ? "Guardando…" : "Guardar PTS →"}</button>
            </>}>
            <div className="space-y-4">
                <div>
                    <label className="overline block mb-1">Tipo de riesgo</label>
                    <select value={tipoRiesgo} onChange={e => setTipoRiesgo(e.target.value)} className="input-atia w-full" data-testid="pts-tipo-riesgo">
                        <option value="alturas">Trabajo en Alturas</option>
                        <option value="espacios_confinados">Espacios Confinados</option>
                        <option value="trabajo_caliente">Trabajo en Caliente</option>
                        <option value="excavaciones">Excavaciones</option>
                        <option value="izaje_cargas">Izaje de Cargas</option>
                    </select>
                </div>
                <div>
                    <label className="overline block mb-1">Descripción del trabajo a realizar</label>
                    <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} rows={2} className="input-atia w-full" data-testid="pts-descripcion" />
                </div>
                <div>
                    <label className="overline block mb-1">Medidas de control (opcional)</label>
                    <textarea value={medidas} onChange={e => setMedidas(e.target.value)} rows={2} className="input-atia w-full" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="overline block mb-1">Hora inicio</label>
                        <input type="time" value={horaInicio} onChange={e => setHoraInicio(e.target.value)} className="input-atia w-full" />
                    </div>
                    <div>
                        <label className="overline block mb-1">Hora fin (vigencia)</label>
                        <input type="time" value={horaFin} onChange={e => setHoraFin(e.target.value)} className="input-atia w-full" />
                    </div>
                </div>

                <SignaturePad label="Firma del SISO — autoriza" value={sisoFirma} onChange={setSisoFirma} testid="firma-siso-pts" />

                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="overline">Trabajadores autorizados ({trabajadores.length})</label>
                        <button type="button" onClick={addTrabajador} className="btn-secondary text-xs flex items-center gap-1"><Plus size={12} /> Agregar trabajador</button>
                    </div>
                    <div className="space-y-3">
                        {trabajadores.map((t, idx) => (
                            <div key={idx} className="border border-zinc-200 p-3">
                                <div className="flex gap-2 mb-2">
                                    <input value={t.nombre} onChange={e => setTrabajador(idx, "nombre", e.target.value)} placeholder="Nombre completo" className="input-atia flex-1 text-sm" />
                                    <input value={t.cedula} onChange={e => setTrabajador(idx, "cedula", e.target.value)} placeholder="Cédula (opcional)" className="input-atia w-40 text-sm" />
                                    {trabajadores.length > 1 && <button type="button" onClick={() => delTrabajador(idx)} className="text-red-500 hover:text-red-700"><Trash size={16} /></button>}
                                </div>
                                <SignaturePad value={t.firma} onChange={(v) => setTrabajador(idx, "firma", v)} testid={`firma-trabajador-${idx}`} compact />
                            </div>
                        ))}
                    </div>
                </div>
                {error && <div className="text-xs text-red-600 font-mono">{error}</div>}
            </div>
        </ModalShell>
    );
}

/* ---- 4) Bitácora HSE (firma única) ---- */
function BitacoraModal({ clientId, onClose, onSaved }) {
    const [numTrabajadores, setNumTrabajadores] = useState("");
    const [novedades, setNovedades] = useState("");
    const [inspecciones, setInspecciones] = useState("");
    const [firma, setFirma] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const valido = firma;

    const submit = async () => {
        setLoading(true); setError("");
        try {
            await api.post("/bitacoras-hse", { client_id: clientId, numero_trabajadores: parseInt(numTrabajadores) || 0, novedades, inspecciones_realizadas: inspecciones, siso_firma: firma });
            onSaved();
        } catch (e) { setError(e.response?.data?.detail || "Error"); setLoading(false); }
    };

    return (
        <ModalShell title="Bitácora / Informe Diario HSE" subtitle="Nuevo registro" onClose={onClose}
            footer={<>
                <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
                <button type="button" onClick={submit} disabled={!valido || loading} className="btn-accent" data-testid="confirm-bitacora">{loading ? "Guardando…" : "Guardar bitácora →"}</button>
            </>}>
            <div className="space-y-4">
                <div>
                    <label className="overline block mb-1">Número de trabajadores en sitio</label>
                    <input type="number" value={numTrabajadores} onChange={e => setNumTrabajadores(e.target.value)} className="input-atia w-40" data-testid="bitacora-num-trabajadores" />
                </div>
                <div>
                    <label className="overline block mb-1">Novedades del día</label>
                    <textarea value={novedades} onChange={e => setNovedades(e.target.value)} rows={3} placeholder="Sin novedades" className="input-atia w-full" data-testid="bitacora-novedades" />
                </div>
                <div>
                    <label className="overline block mb-1">Inspecciones operativas realizadas (opcional)</label>
                    <textarea value={inspecciones} onChange={e => setInspecciones(e.target.value)} rows={2} className="input-atia w-full" />
                </div>
                <SignaturePad label="Firma del SISO" value={firma} onChange={setFirma} testid="firma-bitacora" />
                {error && <div className="text-xs text-red-600 font-mono">{error}</div>}
            </div>
        </ModalShell>
    );
}

/* ---- 5) Reporte de Actos/Condiciones Subestándar (firma única + foto opcional) ---- */
function ActoModal({ clientId, onClose, onSaved }) {
    const [tipo, setTipo] = useState("acto");
    const [descripcion, setDescripcion] = useState("");
    const [correccion, setCorreccion] = useState("");
    const [foto, setFoto] = useState("");
    const [firma, setFirma] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const onFotoChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setFoto(reader.result);
        reader.readAsDataURL(file);
    };

    const valido = descripcion && firma;

    const submit = async () => {
        setLoading(true); setError("");
        try {
            await api.post("/reportes-subestandar", { client_id: clientId, tipo, descripcion, correccion_inmediata: correccion, foto_base64: foto, siso_firma: firma });
            onSaved();
        } catch (e) { setError(e.response?.data?.detail || "Error"); setLoading(false); }
    };

    return (
        <ModalShell title="Reporte de Acto/Condición Subestándar" subtitle="Nuevo reporte" onClose={onClose}
            footer={<>
                <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
                <button type="button" onClick={submit} disabled={!valido || loading} className="btn-accent" data-testid="confirm-acto">{loading ? "Guardando…" : "Guardar reporte →"}</button>
            </>}>
            <div className="space-y-4">
                <div className="flex gap-2">
                    <button type="button" onClick={() => setTipo("acto")} className={`flex-1 p-3 border-2 text-sm font-semibold ${tipo === "acto" ? "border-teal-500 bg-teal-50" : "border-zinc-200"}`}>Acto subestándar</button>
                    <button type="button" onClick={() => setTipo("condicion")} className={`flex-1 p-3 border-2 text-sm font-semibold ${tipo === "condicion" ? "border-teal-500 bg-teal-50" : "border-zinc-200"}`}>Condición subestándar</button>
                </div>
                <div>
                    <label className="overline block mb-1">Descripción del hallazgo</label>
                    <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} rows={3} className="input-atia w-full" data-testid="acto-descripcion" />
                </div>
                <div>
                    <label className="overline block mb-1">Corrección inmediata aplicada (opcional)</label>
                    <textarea value={correccion} onChange={e => setCorreccion(e.target.value)} rows={2} className="input-atia w-full" />
                </div>
                <div>
                    <label className="overline block mb-1">Foto (opcional)</label>
                    <input type="file" accept="image/*" onChange={onFotoChange} className="text-sm" />
                    {foto && <img src={foto} alt="evidencia" className="mt-2 max-h-40 border border-zinc-200" />}
                </div>
                <SignaturePad label="Firma del SISO" value={firma} onChange={setFirma} testid="firma-acto" />
                {error && <div className="text-xs text-red-600 font-mono">{error}</div>}
            </div>
        </ModalShell>
    );
}
